import type { ComponentChildren, TargetedKeyboardEvent, TargetedPointerEvent } from "preact";
import { useRef, useState } from "preact/hooks";
import { cx } from "./lib/cx.ts";

export interface TableColumn<Row> {
  id: string; label: ComponentChildren; render: (row: Row) => ComponentChildren;
  numeric?: boolean; sortable?: boolean;
  /** Takes a share of the table's spare width, so long text gets the room on wide screens and truncates on narrow ones. */
  fill?: boolean;
  /** The person can drag the column's right edge, or use the arrow keys on it, to set its width; a double-click resets it. Best on a `fill` column, whose text truncates. */
  resizable?: boolean;
}
export interface TableSort { column: string; direction: "ascending" | "descending" }
export interface TableGroup<Row> { key: string; title: ComponentChildren; summary?: ComponentChildren; actions?: ComponentChildren; rows: ReadonlyArray<Row> }

/** Matches the stylesheet's floor for a fill column (8rem). */
const MIN_WIDTH = 128;
/** How far an arrow key moves a column's edge. */
const KEY_STEPS = new Map([["ArrowRight", 16], ["ArrowLeft", -16]]);
const clampWidth = (width: number) => Math.max(MIN_WIDTH, Math.round(width));

/** Column widths a person set, kept in this browser under the table's `storageKey`. */
function storedWidths(key: string | undefined): Map<string, number> {
  if (!key) return new Map();
  try {
    const saved: unknown = JSON.parse(localStorage.getItem(`wh-table:${key}`) ?? "{}");
    if (typeof saved !== "object" || saved === null) return new Map();
    return new Map(Object.entries(saved).flatMap(([id, width]) => (typeof width === "number" ? [[id, width] as const] : [])));
  } catch {
    return new Map();
  }
}
function saveWidths(key: string | undefined, widths: ReadonlyMap<string, number>) {
  if (!key) return;
  try {
    localStorage.setItem(`wh-table:${key}`, JSON.stringify(Object.fromEntries(widths)));
  } catch {
    // Storage is blocked: the widths last for this visit.
  }
}

// See Design > UI, "Curator Studio assets". Semantic table; the caller owns sorting, grouping, fetching and URL state.
// Pass `groups` instead of `rows` to split the body into labelled row groups, each with its own actions.
// Give `storageKey` to remember the widths a person sets on `resizable` columns.
export function DataTable<Row>({ caption, columns, rows = [], groups, rowKey, sort, onSort, storageKey }: {
  caption: string; columns: ReadonlyArray<TableColumn<Row>>; rows?: ReadonlyArray<Row>; groups?: ReadonlyArray<TableGroup<Row>>;
  rowKey: (row: Row) => string; sort?: TableSort; onSort?: (sort: TableSort) => void; storageKey?: string;
}) {
  const [widths, setWidths] = useState(() => storedWidths(storageKey));
  const latest = useRef(widths);
  const [dragging, setDragging] = useState<string | null>(null);
  const change = (id: string, width: number | null, save: boolean) => {
    const next = new Map(latest.current);
    if (width === null) next.delete(id); else next.set(id, clampWidth(width));
    latest.current = next;
    setWidths(next);
    if (save) saveWidths(storageKey, next);
  };
  // Fill columns nobody has sized share the spare width equally.
  const sharing = columns.filter((c) => c.fill && !widths.has(c.id)).length;
  const widthStyle = (column: TableColumn<Row>) => {
    const set = widths.get(column.id);
    if (set !== undefined) return { width: `${set}px`, minWidth: `${set}px` };
    return column.fill ? { width: `${100 / sharing}%` } : undefined;
  };

  const startDrag = (event: TargetedPointerEvent<HTMLSpanElement>, id: string) => {
    const handle = event.currentTarget;
    const cell = handle.parentElement;
    if (!cell || event.button !== 0) return;
    event.preventDefault();
    handle.setPointerCapture(event.pointerId);
    const startX = event.clientX;
    const startWidth = cell.getBoundingClientRect().width;
    setDragging(id);
    const move = (e: PointerEvent) => change(id, startWidth + e.clientX - startX, false);
    const end = () => {
      handle.removeEventListener("pointermove", move);
      handle.removeEventListener("pointerup", end);
      handle.removeEventListener("pointercancel", end);
      setDragging(null);
      saveWidths(storageKey, latest.current);
    };
    handle.addEventListener("pointermove", move);
    handle.addEventListener("pointerup", end);
    handle.addEventListener("pointercancel", end);
  };
  const onKey = (event: TargetedKeyboardEvent<HTMLSpanElement>, id: string) => {
    const step = KEY_STEPS.get(event.key);
    const cell = event.currentTarget.parentElement;
    if (!step || !cell) return;
    event.preventDefault();
    change(id, (latest.current.get(id) ?? cell.getBoundingClientRect().width) + step, true);
  };

  const body = (list: ReadonlyArray<Row>) => list.map(row => <tr key={rowKey(row)}>{columns.map(column => <td key={column.id} class={cx(column.numeric && "ui-table__num", column.fill && "ui-table__fill")}>{column.render(row)}</td>)}</tr>);
  return <div class={cx("ui-table-wrap", dragging && "is-resizing")} role="region" aria-label={caption} tabIndex={0}>
    <table class="ui-table"><caption class="ui-sr-only">{caption}</caption>
      <thead><tr>{columns.map(column => <th key={column.id} scope="col" style={widthStyle(column)} class={cx(column.numeric && "ui-table__num", column.fill && "ui-table__fill")} aria-sort={column.sortable ? (sort?.column === column.id ? sort.direction : "none") : undefined}>
        {column.sortable && onSort ? <button type="button" onClick={() => onSort({ column: column.id, direction: sort?.column === column.id && sort.direction === "ascending" ? "descending" : "ascending" })}>
          {column.label}<span aria-hidden="true">{sort?.column === column.id ? (sort.direction === "ascending" ? "↑" : "↓") : "↕"}</span>
        </button> : column.label}
        {column.resizable && <span class={cx("ui-table__resize", dragging === column.id && "is-active")} role="separator" aria-orientation="vertical" tabIndex={0}
          aria-label={`Resize ${typeof column.label === "string" ? column.label : column.id}`} aria-valuemin={MIN_WIDTH}
          aria-valuenow={widths.get(column.id)} aria-valuetext={widths.has(column.id) ? `${widths.get(column.id)} pixels` : "Automatic width"}
          title="Drag to resize; double-click to reset" onPointerDown={(e) => startDrag(e, column.id)} onKeyDown={(e) => onKey(e, column.id)} onDblClick={() => change(column.id, null, true)} />}
      </th>)}</tr></thead>
      {groups ? groups.map(group => <tbody key={group.key}>
        <tr class="ui-table__group"><th scope="rowgroup" colSpan={columns.length}><div class="ui-table__group-head">
          <span class="ui-table__group-title">{group.title}</span>{group.summary && <span class="ui-table__group-summary">{group.summary}</span>}{group.actions && <span class="ui-table__group-actions">{group.actions}</span>}
        </div></th></tr>
        {body(group.rows)}
      </tbody>) : <tbody>{body(rows)}</tbody>}
    </table>
  </div>;
}
